/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */


import { ElementRef, Inject, Injectable, NgZone, Renderer2, DOCUMENT } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class NgfA11yService {

  private focusStack: ElementRef<HTMLElement>[] = [new ElementRef(this.document.activeElement as HTMLElement)];

  constructor(private zone: NgZone,
              @Inject(DOCUMENT) private document: Document) { }

  focusElement(elementRef: ElementRef<HTMLElement>) {
    const currentFocusedElement = new ElementRef(this.document.activeElement as HTMLElement);
    this.focusStack.push(currentFocusedElement);
    this.focusStack.push(elementRef);
    elementRef.nativeElement.focus();
  }

  focusPreviousElement(): void {
    if (this.focusStack.length > 1) {
      this.focusStack.pop();
      const previousElement = this.focusStack.pop();
      previousElement.nativeElement.focus();
    }
  }

  getPreviousFocusedElement(): ElementRef<HTMLElement> | null {
    if (this.focusStack.length > 1) {
      return this.focusStack[this.focusStack.length - 2];
    }
    return null;
  }

  getCurrentFocusedElement(): ElementRef<HTMLElement> | null {
    if (this.focusStack.length > 0) {
      return this.focusStack[this.focusStack.length - 1];
    }
    return null;
  }

  prepareClickableElement(elementRef: ElementRef<HTMLElement>, renderer: Renderer2, options?: { debounceClicks?: boolean }): void {
    // we have to wait for all attributes to be setup by (possible) other directives
    setTimeout(() => {
      if (elementRef.nativeElement instanceof HTMLButtonElement) {
        this.prepareButtonElement(elementRef.nativeElement, renderer, options?.debounceClicks ?? true);
      } else if (elementRef.nativeElement instanceof HTMLAnchorElement) {
        this.prepareAnchorElement(elementRef.nativeElement, renderer);
      } else {
        throw Error(`[NgfA11yService] Can set up only <button> or <a>, got: ${elementRef.nativeElement.tagName.toLowerCase()}`);
      }
    });
  }

  private prepareButtonElement(button: HTMLButtonElement, renderer: Renderer2, debounceClicks: boolean): void {
    this.setDefault(renderer, button, 'type', 'button');
    if (debounceClicks) {
      this.preventMultipleClick(button);
    }
  }

  private preventMultipleClick(button: HTMLButtonElement): void {
    const DEBOUNCE_TIME = 500;

    this.zone.runOutsideAngular(() => {
      button['__prevClickEventTimestamp'] = 0;

      button.addEventListener('click', event => {
        const elapsedTime = event.timeStamp - button['__prevClickEventTimestamp'];
        if (elapsedTime < DEBOUNCE_TIME) {
          event.stopPropagation();
          event.preventDefault();
        }

        button['__prevClickEventTimestamp'] = event.timeStamp;
      }, { capture: true });
    });
  }

  private prepareAnchorElement(anchor: HTMLAnchorElement, renderer: Renderer2): void {
    if (this.isExternalLink(anchor)) {
      this.setDefault(renderer, anchor, 'target', '_blank');
      this.setDefault(renderer, anchor, 'rel', 'external noopener noreferrer');
    }
  }

  private isExternalLink(anchor: HTMLAnchorElement): boolean {
    try {
      const href = new URL(anchor.href);
      return href.hostname !== this.document.location.hostname;
    } catch {
      console.error(`[NgfA11yService] Invalid URL in href: ${anchor.href}`);
      return false;
    }

  }

  private setDefault(renderer: Renderer2, element: HTMLElement, attribute: string, value: string): void {
    if (element.hasAttribute(attribute)) {
      return;
    }

    renderer.setAttribute(element, attribute, value);
  }
}
